import type { ComponentType, ReactNode } from 'react';
import { ShieldX } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

export interface PermissionStateProps {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  busy?: boolean;
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  titleId?: string;
  mainAriaLabel?: string;
  className?: string;
}

/**
 * Shared access boundary for authentication and permission failures.
 *
 * The component owns only the presentation and landmark contract. Callers
 * still own the authorization decision, redirect target, and recovery action.
 */
export function PermissionState({
  title,
  description,
  action,
  busy = false,
  icon: Icon = ShieldX,
  titleId = 'permission-state-title',
  mainAriaLabel,
  className,
}: PermissionStateProps) {
  if (busy) {
    return (
      <main
        id="main-content"
        aria-label={mainAriaLabel ?? title}
        aria-busy="true"
        tabIndex={-1}
        className={cn('min-h-screen flex items-center justify-center bg-background', className)}
      >
        <div className="space-y-4 text-center">
          <div
            aria-hidden="true"
            className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary"
          />
          <p className="text-lg font-medium" role="status" aria-live="polite">
            {title}
          </p>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </main>
    );
  }

  return (
    <main
      id="main-content"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={cn('min-h-screen flex items-center justify-center bg-background p-4', className)}
    >
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <Icon aria-hidden="true" className="h-8 w-8 text-red-600" />
        </div>
        <h1 id={titleId} className="text-2xl font-bold text-foreground">
          {title}
        </h1>
        {description ? (
          <Alert className="border-red-200 bg-red-50">
            <Icon aria-hidden="true" className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{description}</AlertDescription>
          </Alert>
        ) : null}
        {action ? <div>{action}</div> : null}
      </div>
    </main>
  );
}
